export interface PersonalManager {
  id: string;
  fullName: string;
  avatarUrl: string;
  phone: string | null;
  email: string | null;
  telegram: string | null;
  whatsapp: string | null;
  enabled: boolean;
}

type RawObject = Record<string, unknown>;

const pick = (raw: RawObject, camel: string, snake: string): unknown => raw[camel] ?? raw[snake];

const asFilledString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

const asObject = (value: unknown): RawObject | null =>
  value !== null && typeof value === 'object' ? (value as RawObject) : null;

/**
 * Extracts the personal manager from a /users/me payload.
 *
 * UserResponse.v1 specifies snake_case keys (vip_info.personal_manager.full_name) while the
 * public serializer camelCases the other user fields, so both shapes are accepted here —
 * a serializer difference between environments must not silently hide the manager.
 *
 * Returns null when the user has no VIP assignment (vip_info: null) or the payload carries
 * no usable manager (a manager without a name is treated as absent).
 */
export function parseVipManager(payload: unknown): PersonalManager | null {
  const source = asObject(payload);
  if (source === null) {
    return null;
  }

  const vipInfo = asObject(pick(source, 'vipInfo', 'vip_info'));
  if (vipInfo === null) {
    return null;
  }

  const manager = asObject(pick(vipInfo, 'personalManager', 'personal_manager'));
  if (manager === null) {
    return null;
  }

  const fullName = asFilledString(pick(manager, 'fullName', 'full_name'));
  if (fullName === null) {
    return null;
  }

  return {
    id: asFilledString(manager.id) ?? '',
    fullName,
    avatarUrl: asFilledString(pick(manager, 'avatarUrl', 'avatar_url')) ?? '',
    phone: asFilledString(manager.phone),
    email: asFilledString(manager.email),
    telegram: asFilledString(manager.telegram),
    whatsapp: asFilledString(manager.whatsapp),
    enabled: manager.enabled !== false,
  };
}
